import styled from '@emotion/styled';
import type { ObjectId } from '@racemap/sdk/schema/base';
import type { EntityEvent } from '@racemap/sdk/schema/entityEvents';
import type { Tracker, TrackerState } from '@racemap/sdk/schema/trackers';
import { HAIRSP } from '@racemap/utilities/consts/characters';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { LogisticsState, TRACKER_TYPES } from '@racemap/utilities/consts/trackers';
import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import type { PathInto } from '@racemap/utilities/types/utils';
import { Space, Tag, Tooltip, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import { type FC, useEffect, useState } from 'react';
import { PopoverHint } from 'src/components/BasicComponents';
import {
  useConfigurationUpdateResult,
  useFirmwareUpdateResult,
  useIsAdmin,
} from 'src/lib/customHooks';
import { useStore } from '../../../store/reducers';
import { DownloadableQRCodePopover } from '../../BasicComponents/DownloadableQRCodePopover.tsx/DownloadableQRCodePopover';
import { type Column, type Entry, SimpleTable } from '../../BasicComponents/SimpleTable';
import { IconExternal, IconInStock, IconOutOfStock } from '../../Icon';
import { castDateTime } from '../../utils/timeUtils';
import { LogisticsFilters } from '../Filter/FilterTreeFunctions';
import { LogisticsFilterLabels } from '../Filter/prepareFilterSubTreeComponentMapping';
import { ConfigurationUpdateResult } from './ConfigurationUpdateResult';
import { FirmwareUpdateResult } from './FirmwareUpdateResult';

export type Props = {
  tracker: Immutable<Tracker>;
};
const { Text } = Typography;

interface TrackerInfos extends TrackerState {
  id: ObjectId;
  imei: string;
  name: string;
  type: TRACKER_TYPES;
  iccid: string;
  fwVersion: string;
  configUpdateResult?: EntityEvent;
  firmwareUpdateResult?: EntityEvent;
  logistics?: LogisticsState;
  landingPage: string;
  notes: string;
}
interface SettingsEntry extends Entry {
  id: keyof TrackerInfos;
  value: string | number | boolean;
}

export const TrackerInfosTable: FC<Props> = ({ tracker }) => {
  const { updateTrackers } = useStore((s) => s.trackers.actions);
  const isAdmin = useIsAdmin();
  const [trackerNotes, setTrackerNotes] = useState<string>(tracker.notes);
  const { configUpdateResult } = useConfigurationUpdateResult(tracker.id.toHexString());
  const { firmwareUpdateResult } = useFirmwareUpdateResult(tracker.id.toHexString());

  useEffect(() => {
    setTrackerNotes(tracker.notes);
  }, [tracker.notes]);

  if (tracker == null) return <></>;

  const trackerInfos: TrackerInfos = {
    id: tracker.id,
    name: tracker.trackerName,
    type: tracker.trackerType,
    imei: tracker.trackerId,
    iccid: tracker.meta?.iccid || 'unknown',

    fwVersion: tracker.meta?.firmwareVersion || 'unknown',
    logistics: tracker.logistics,
    landingPage: `/tracker/${tracker.id}`,
    ...(configUpdateResult ? { configUpdateResult } : {}),
    ...(firmwareUpdateResult ? { firmwareUpdateResult } : {}),

    ...tracker.state,
    notes: tracker.notes,
  };
  const data = Object.entries(trackerInfos).map(([key, value]) => ({
    value,
    id: key,
  })) as Array<SettingsEntry>;

  const columns = [
    {
      header: 'Info',
      dataKey: 'id',
      cellRenderer: (c, r) => {
        if (isNotEmptyString(r.id) && TrackerInfosLable[r.id]) {
          const description = TrackerSettingsDescription[r.id];
          if (description) {
            return <PopoverHint text={TrackerInfosLable[r.id]}>{description}</PopoverHint>;
          }
          return TrackerInfosLable[r.id];
        }
        return c;
      },
      key: 'id',
    } as Column<SettingsEntry, 'id'>,
    {
      header: 'Value',
      dataKey: 'value',
      cellRenderer: (c, r) => {
        if (c == null) return;
        switch (r.id) {
          case 'type':
            return <Tag>{c.toString()}</Tag>;
          case 'id':
          case 'imei':
          case 'fwVersion':
            return (
              <Text copyable code>
                {c.toString()}
              </Text>
            );
          case 'configUpdateResult': {
            if (typeof c !== 'object' || !c) return null;
            return <ConfigurationUpdateResult event={c as EntityEvent} />;
          }
          case 'firmwareUpdateResult': {
            if (typeof c !== 'object' || !c) return null;
            return <FirmwareUpdateResult event={c as EntityEvent} />;
          }
          case 'iccid':
            return (
              <Text
                code
                copyable
                editable={
                  isAdmin
                    ? {
                        onChange: (newValue) =>
                          updateTrackers([tracker.id], {
                            meta: { [r.id]: newValue },
                          }),
                      }
                    : undefined
                }
              >
                {c.toString()}
              </Text>
            );
          case 'name':
            return (
              <Text
                editable={{
                  onChange: (newValue) => updateTrackers([tracker.id], { trackerName: newValue }),
                }}
              >
                {c.toString()}
              </Text>
            );
          case 'landingPage':
            if (typeof c !== 'string') {
              return null;
            }
            return (
              <Space size={4}>
                <Text copyable={{ text: `${window.origin}${c}` }}>...{c}</Text>
                <Tooltip title="Open Page">
                  <ExternalIcon onClick={() => window.open(`${window.origin}${c}`, '_blank')} />
                </Tooltip>
                <DownloadableQRCodePopover
                  landingPage={`${window.origin}${c}`}
                  iconColor={RacemapColors.PaleBlue}
                />
              </Space>
            );
          case 'battery':
            return `${c}${HAIRSP}%`;
          case 'batteryVoltage':
            return `${c}${HAIRSP}V`;
          case 'temperature':
            return `${c}${HAIRSP}°C`;
          case 'logistics':
            if (c === LogisticsFilters.IN_STOCK)
              return (
                <Tag>
                  <Space>
                    <IconInStock />
                    {LogisticsFilterLabels[LogisticsFilters.IN_STOCK]}
                  </Space>
                </Tag>
              );
            if (c === LogisticsFilters.OUT_OF_STOCK)
              return (
                <Tag>
                  <Space>
                    <IconOutOfStock />
                    {LogisticsFilterLabels[LogisticsFilters.OUT_OF_STOCK]}
                  </Space>
                </Tag>
              );
            if (c == null) return '--';
            return c;
          case 'lastConnectedAt':
          case 'lastDisconnectedAt':
          case 'lastDataAt':
            if (typeof c === 'boolean') return '--';
            return castDateTime(c).toLocaleString(DateTime.DATETIME_FULL);
          case 'notes':
            return (
              <TextArea
                onBlur={() => updateTrackers([tracker.id], { notes: trackerNotes })}
                value={trackerNotes}
                onChange={(e) => setTrackerNotes(e.target.value)}
                autoSize={{ minRows: 3 }}
                placeholder={'Add notes here...'}
              />
            );
          default:
            return `${c}`;
        }
      },
      key: 'value',
    } as Column<SettingsEntry, 'value'>,
  ] as Array<Column<Entry, PathInto<SettingsEntry>>>;

  return <Table columns={columns} data={data} />;
};

const Table = styled(SimpleTable)`
  border-radius: unset;
  border-top: unset;

  .external-icon {
    height: 14px;
    color: ${RacemapColors.PaleBlue};
    cursor: pointer;

    :hover {
      opacity: 0.8;
    }
  }
`;

const ExternalIcon = styled(IconExternal)`
  color: ${RacemapColors.PaleBlue};
  cursor: pointer;
  height: 13px;

  :hover {
    opacity: 0.8;
  }
`;

const TrackerInfosLable: Record<keyof TrackerInfos, string> = {
  id: 'ID',
  imei: 'IMEI',
  name: 'Name',
  type: 'Type',
  iccid: 'ICCID',
  fwVersion: 'FW Version',
  configUpdateResult: 'Config Update Result',
  firmwareUpdateResult: 'Firmware Update Result',
  landingPage: 'Tracker Overview',
  notes: 'Notes',
  lastConnectedAt: 'Connected at',
  lastDisconnectedAt: 'Disconnected at',
  disconnectReason: 'Disconnect Reason',
  lastDataAt: 'Last Data at',
  battery: 'Battery State',
  batteryVoltage: 'Battery Voltage',
  charging: 'Is Charging',
  gpsActiv: 'GPS Activated',
  gpsFix: 'Location Fix',
  isDataPossible: 'Mobile Data',
  registered: 'Network Registered',
  movement: 'Is Moving',
  signalStrength: 'Signal Strength',
  temperature: 'Temperature',
  logistics: 'Logistics',
  heartbeatLastAt: 'Last Heartbeat',
  infoReportLastAt: 'Last Info Report',
};

const TrackerSettingsDescription: Partial<Record<keyof TrackerInfos, string>> = {
  lastConnectedAt:
    'Timestamp when the tracker last established a socket connection with the RACEMAP server.',
  lastDisconnectedAt:
    'Timestamp when the tracker last closed its socket connection with the RACEMAP server. Note: The tracker may stop communicating without explicitly closing the socket.',
  lastDataAt:
    'Timestamp when the RACEMAP server last received data from the tracker, including geolocation information, acknowledgments, heartbeats, or status reports.',
};
